import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Play, Pause, SkipBack, SkipForward, Maximize, Minimize, Plus, Trash2,
  Loader2, Lock, Mic, Music, Timer, Import, Hand, X, Cast, Copy, Square,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { WordSyncDisplay, totalDurationMs, currentLineIndex } from "@/components/WordSyncDisplay";

const fmtT = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const SOURCE_LABELS = {
  own_content: "Own content",
  manual: "Manual entry",
};

export default function KaraokePrompter() {
  const [tracks, setTracks] = useState([]);
  const [track, setTrack] = useState(null);
  const [license, setLicense] = useState({ license_confirmed: false });
  const [importSources, setImportSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  // playback clock
  const [playing, setPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [fontSize, setFontSize] = useState(44);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const audioRef = useRef(null);
  const panelRef = useRef(null);
  const clockRef = useRef({ raf: null, last: 0 });

  // tap sync
  const [tapMode, setTapMode] = useState(false);
  const [tapIndex, setTapIndex] = useState(0);
  const tapsRef = useRef([]);

  // karaoke night (live QR session)
  const [live, setLive] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveBusy, setLiveBusy] = useState(false);

  const lines = track?.lines || [];
  const durationMs = useMemo(
    () => Math.max(totalDurationMs(lines), (track?.duration_s || 0) * 1000),
    [lines, track?.duration_s]
  );
  const allWords = useMemo(() => lines.flatMap((l) => l.line_text.split(/\s+/).filter(Boolean)), [lines]);
  const hasAudio = !!track?.audio_reference;

  const load = async () => {
    try {
      const [t, lic, imp, lv] = await Promise.all([
        api.get("/karaoke-prompter/tracks"),
        api.get("/karaoke-prompter/license"),
        api.get("/karaoke-prompter/import-sources"),
        api.get("/karaoke-prompter/live").catch(() => ({ data: { active: false } })),
      ]);
      setTracks(t.data.tracks);
      setLicense(lic.data);
      setImportSources(imp.data.sources);
      if (lv.data.live_id) setLive(lv.data);
      return t.data.tracks;
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // internal clock (no-audio mode + tap mode)
  useEffect(() => {
    if (!playing || (hasAudio && !tapMode)) return;
    let raf, last = performance.now();
    const step = (t) => {
      setCurrentMs((ms) => ms + (t - last));
      last = t;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, hasAudio, tapMode]);

  const openTrack = async (id) => {
    stopAll();
    const { data } = await api.get(`/karaoke-prompter/tracks/${id}`);
    setTrack(data);
    setCurrentMs(0);
  };

  // ── karaoke night: go live / stop / heartbeat ──
  const goLive = async () => {
    if (!track) return;
    setLiveBusy(true);
    try {
      const { data } = await api.post("/karaoke-prompter/live/start", { track_id: track.track_id });
      setLive((l) => ({ ...(l || {}), ...data, active: true }));
      setLiveOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the live session"); }
    finally { setLiveBusy(false); }
  };

  const stopLive = async () => {
    try {
      await api.post("/karaoke-prompter/live/stop");
      setLive((l) => (l ? { ...l, active: false } : l));
      setLiveOpen(false);
      toast.success("Karaoke night ended — listeners now see it as wrapped");
    } catch { toast.error("Could not stop the session"); }
  };

  const hbRef = useRef({ ms: 0, playing: false });
  hbRef.current = { ms: currentMs, playing };
  useEffect(() => {
    if (!live?.active) return;
    const t = setInterval(() => {
      api.post("/karaoke-prompter/live/heartbeat",
        { position_ms: Math.round(hbRef.current.ms), playing: hbRef.current.playing }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [live?.active]);

  const stopAll = () => {
    setPlaying(false);
    setTapMode(false);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  };

  const togglePlay = () => {
    if (!track) return;
    if (hasAudio && !tapMode) {
      const a = audioRef.current;
      if (!a) return;
      if (playing) a.pause(); else a.play().catch(() => toast.error("Could not play the audio reference"));
      setPlaying(!playing);
    } else {
      setPlaying((p) => !p);
    }
  };

  const seekMs = (ms) => {
    const v = Math.max(0, ms);
    setCurrentMs(v);
    if (audioRef.current && hasAudio) audioRef.current.currentTime = v / 1000;
  };

  const jumpLine = (dir) => {
    const synced = lines.filter((l) => (l.word_timings || []).length);
    if (!synced.length) return;
    const li = currentLineIndex(synced, currentMs);
    const target = synced[Math.min(synced.length - 1, Math.max(0, li + dir))];
    if (target?.word_timings?.length) seekMs(target.word_timings[0].start_ms);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else panelRef.current?.requestFullscreen?.();
  };

  // ── BPM estimate ──
  const [estBpm, setEstBpm] = useState("");
  const [estDur, setEstDur] = useState("");
  const [estimating, setEstimating] = useState(false);
  const runEstimate = async () => {
    setEstimating(true);
    try {
      const { data } = await api.post(`/karaoke-prompter/tracks/${track.track_id}/estimate`, {
        bpm: estBpm ? parseInt(estBpm, 10) : null,
        duration_s: estDur ? parseFloat(estDur) : null,
      });
      setTrack(data);
      toast.success("Rough timing estimated — fine-tune with tap sync if needed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not estimate"); }
    finally { setEstimating(false); }
  };

  // ── Tap sync ──
  const startTapSync = () => {
    if (!allWords.length) { toast.error("Add lyrics first"); return; }
    tapsRef.current = [];
    setTapIndex(0);
    setTapMode(true);
    setCurrentMs(0);
    if (hasAudio && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
    setPlaying(true);
  };

  const tap = () => {
    const ms = hasAudio && audioRef.current && !tapMode ? audioRef.current.currentTime * 1000 : currentMs;
    tapsRef.current.push(Math.round(hasAudio && audioRef.current ? audioRef.current.currentTime * 1000 : ms));
    const next = tapIndex + 1;
    setTapIndex(next);
    if (next >= allWords.length) finishTapSync();
  };

  const finishTapSync = async () => {
    setPlaying(false);
    setTapMode(false);
    if (audioRef.current) audioRef.current.pause();
    const taps = tapsRef.current;
    let wi = 0;
    const newLines = lines.map((l) => {
      const words = l.line_text.split(/\s+/).filter(Boolean);
      const wt = words.map((w) => {
        const start = taps[wi] ?? (taps[taps.length - 1] || 0);
        const end = taps[wi + 1] ?? start + 500;
        wi += 1;
        return { word: w, start_ms: start, end_ms: end };
      });
      return { ...l, word_timings: wt };
    });
    try {
      const { data } = await api.patch(`/karaoke-prompter/tracks/${track.track_id}`, { lines: newLines });
      setTrack(data);
      setCurrentMs(0);
      toast.success(`Tap sync saved — ${taps.length} words marked`);
    } catch { toast.error("Could not save tap timings"); }
  };

  const deleteTrack = async (t) => {
    await api.delete(`/karaoke-prompter/tracks/${t.track_id}`);
    setTracks((l) => l.filter((x) => x.track_id !== t.track_id));
    if (track?.track_id === t.track_id) setTrack(null);
  };

  const importJob = async (job_id) => {
    try {
      const { data } = await api.post(`/karaoke-prompter/import/${job_id}`);
      toast.success(`Imported "${data.title}" with pre-synced word timing`);
      const list = await load();
      setTrack(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  if (loading) return <div className="p-10 font-mono text-xs text-ink-muted">LOADING KARAOKE PROMPTER...</div>;

  return (
    <div className="p-6 md:p-10 max-w-[1500px] mx-auto animate-fade-up" data-testid="karaoke-prompter-page">
      <PageHeader
        eyebrow="// KARAOKE PROMPTER"
        title="Every word, right on the beat."
        description="Word-by-word lyric sync for on-air karaoke and live sing-alongs. Displays timed text only — your mixer handles all audio."
      />

      <div className="grid lg:grid-cols-[360px,1fr] gap-4">
        {/* ── LEFT: source + tracks + timing ── */}
        <div className="space-y-4 self-start">
          {/* Lyric source tiers */}
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="lyric-source-panel">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">// lyric source</div>
            <div className="space-y-2">
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  Your own content
                  <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-mono uppercase tracking-wider text-emerald-300">Free</span>
                </div>
                <div className="text-[11px] text-ink-secondary mt-1">Station jingles, original songs, AI Music Studio tracks — content you already hold rights to.</div>
              </div>
              <div className="rounded-lg border border-line bg-bg-elevated p-3">
                <div className="text-sm font-semibold">Manual entry</div>
                <div className="text-[11px] text-ink-secondary mt-1">Paste lyrics you have display rights for, then sync timing yourself. Rights responsibility stays with your station — BroadcastFlow AI doesn't fetch or clear anything.</div>
              </div>
              <div className="rounded-lg border border-line bg-bg-elevated p-3 opacity-60" data-testid="musixmatch-locked">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Lock className="h-3.5 w-3.5 text-brand-amber" /> Musixmatch (licensed)
                  <span className="rounded bg-brand-amber/15 px-1.5 py-0.5 text-[8px] font-mono uppercase tracking-wider text-brand-amber">Locked</span>
                </div>
                <div className="text-[11px] text-ink-secondary mt-1">
                  Auto-fetched, pre-synced commercial lyrics require a paid Musixmatch commercial display license.
                  {license.license_confirmed
                    ? " License on file — contact support to activate the integration."
                    : " This stays locked until your station confirms a license on file — displaying commercial lyrics without one isn't something we'll help you do."}
                </div>
              </div>
            </div>
          </div>

          {/* Tracks */}
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="karaoke-tracks-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// tracks</div>
              <button onClick={() => setCreateOpen(true)} data-testid="karaoke-new-track-btn"
                className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs text-ink-secondary hover:text-brand-amber transition">
                <Plus className="h-3 w-3" /> New track
              </button>
            </div>
            {tracks.length === 0 ? (
              <div className="text-xs text-ink-muted py-4 text-center border border-dashed border-line rounded-lg">
                No tracks yet — create one or import from Karaoke Studio below.
              </div>
            ) : (
              <div className="space-y-1.5">
                {tracks.map((t) => (
                  <div key={t.track_id} onClick={() => openTrack(t.track_id)}
                    data-testid={`karaoke-track-${t.track_id}`}
                    className={`group cursor-pointer rounded-lg border p-3 transition ${
                      track?.track_id === t.track_id ? "border-brand-amber bg-brand-amber/5" : "border-line bg-bg-elevated hover:border-brand-amber/40"
                    }`}>
                    <div className="flex items-center gap-2">
                      <Music className="h-3.5 w-3.5 text-ink-muted shrink-0" />
                      <div className="flex-1 min-w-0 text-sm font-semibold truncate">{t.title}</div>
                      {t.synced && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-mono uppercase text-emerald-300">Synced</span>}
                      <button onClick={(e) => { e.stopPropagation(); deleteTrack(t); }}
                        data-testid={`karaoke-track-delete-${t.track_id}`}
                        className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-rose-400 transition">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="ml-5 mt-1 text-[10px] font-mono text-ink-muted">
                      {SOURCE_LABELS[t.source_type] || t.source_type} · {t.line_count} lines {t.bpm ? `· ${t.bpm} BPM` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {importSources.length > 0 && (
              <div className="mt-3 border-t border-line pt-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">// import from karaoke studio (pre-synced)</div>
                {importSources.map((s) => (
                  <button key={s.job_id} onClick={() => importJob(s.job_id)} data-testid={`karaoke-import-${s.job_id}`}
                    className="w-full flex items-center gap-2 rounded-lg border border-line bg-bg-elevated p-2.5 mb-1.5 text-left hover:border-brand-amber/50 transition">
                    <Import className="h-3.5 w-3.5 text-cyan-300 shrink-0" />
                    <span className="flex-1 min-w-0 text-xs font-semibold truncate">{s.label}</span>
                    <span className="text-[9px] font-mono text-ink-muted">{s.word_count} words</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Timing */}
          {track && (
            <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="timing-panel">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">// timing sync</div>
              {tapMode ? (
                <div className="text-center">
                  <div className="text-xs text-ink-secondary mb-2">Tap in time with each word ({tapIndex}/{allWords.length})</div>
                  <div className="text-lg font-display font-bold mb-3 text-brand-amber">{allWords[tapIndex] || "—"}</div>
                  <button onClick={tap} data-testid="tap-btn"
                    className="w-full rounded-xl bg-brand-amber py-6 text-xl font-black text-black amber-glow active:scale-95 transition">
                    TAP
                  </button>
                  <button onClick={() => { setTapMode(false); setPlaying(false); if (audioRef.current) audioRef.current.pause(); }}
                    data-testid="tap-cancel-btn"
                    className="mt-2 text-xs text-ink-muted hover:text-rose-400">Cancel tap sync</button>
                </div>
              ) : (
                <>
                  <button onClick={startTapSync} data-testid="tap-sync-start-btn"
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand-amber/50 bg-brand-amber/10 py-2.5 text-sm font-semibold text-brand-amber hover:bg-brand-amber/20 transition mb-3">
                    <Hand className="h-4 w-4" /> Tap-to-mark sync
                  </button>
                  <div className="flex items-center gap-2">
                    <input value={estBpm} onChange={(e) => setEstBpm(e.target.value)} placeholder={track.bpm ? `${track.bpm} BPM` : "BPM"}
                      data-testid="est-bpm-input"
                      className="w-20 rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs outline-none focus:border-brand-amber/60" />
                    <span className="text-[10px] text-ink-muted">or</span>
                    <input value={estDur} onChange={(e) => setEstDur(e.target.value)} placeholder="secs"
                      data-testid="est-dur-input"
                      className="w-16 rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs outline-none focus:border-brand-amber/60" />
                    <button onClick={runEstimate} disabled={estimating} data-testid="est-run-btn"
                      className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-cyan-500/50 bg-cyan-500/10 px-2 py-1.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-500/20 transition disabled:opacity-50">
                      {estimating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Timer className="h-3 w-3" />} Estimate
                    </button>
                  </div>
                  <div className="mt-2 text-[10px] text-ink-muted">A rough starting point, not perfect — refine with tap sync.</div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT: prompter ── */}
        <div ref={panelRef} className="rounded-xl border border-line bg-bg-surface overflow-hidden flex flex-col" data-testid="karaoke-prompter-panel">
          <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
            <div className="min-w-0">
              <div className="font-display font-bold truncate">{track ? track.title : "No track selected"}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                {track ? `${SOURCE_LABELS[track.source_type] || track.source_type}${hasAudio ? " · audio linked" : " · clock mode"}` : "pick a track on the left"}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {track?.bpm && (
                <span className="rounded border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-mono text-cyan-300" data-testid="bpm-chip">
                  {track.bpm} BPM
                </span>
              )}
              <span className="font-mono text-sm tabular-nums" data-testid="karaoke-elapsed">
                {fmtT(currentMs)} <span className="text-ink-muted">/ {fmtT(durationMs)}</span>
              </span>
              {track && (
                <button
                  onClick={() => (live?.active && live?.track_id === track.track_id ? setLiveOpen(true) : goLive())}
                  disabled={liveBusy}
                  data-testid="karaoke-golive-btn"
                  title="Karaoke Night: get a QR code listeners scan to follow the synced lyrics on their phones"
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
                    live?.active
                      ? "border-rose-500/60 bg-rose-500/10 text-rose-300"
                      : "border-line bg-bg-elevated text-ink-secondary hover:text-brand-amber"
                  }`}>
                  {liveBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                    <>
                      {live?.active && <span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-pulse" />}
                      <Cast className="h-3 w-3" />
                    </>
                  )}
                  {live?.active ? "LIVE — QR" : "Go live"}
                </button>
              )}
              <span className="inline-flex items-center gap-1.5 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-mono text-emerald-300" data-testid="mic-status">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> <Mic className="h-3 w-3" /> Mic: your mixer
              </span>
            </div>
          </div>

          <div className="relative bg-black flex items-center justify-center" style={{ height: isFullscreen ? "calc(100vh - 128px)" : 440 }}>
            {track ? (
              <WordSyncDisplay lines={lines} currentMs={currentMs} fontSize={fontSize} />
            ) : (
              <div className="text-ink-muted text-xl">Select or create a track to start.</div>
            )}
          </div>

          {/* progress */}
          <div className="h-1.5 bg-bg-elevated">
            <div className="h-full bg-brand-amber/70 transition-[width] duration-200" style={{ width: durationMs ? `${Math.min(100, (currentMs / durationMs) * 100)}%` : "0%" }} data-testid="karaoke-progress" />
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-line px-5 py-3">
            <div className="flex items-center gap-2">
              <button onClick={() => jumpLine(-1)} data-testid="karaoke-prev-line" title="Previous line"
                className="rounded-md border border-line bg-bg-elevated p-2 text-ink-secondary hover:text-brand-amber transition">
                <SkipBack className="h-4 w-4" />
              </button>
              <button onClick={togglePlay} disabled={!track || tapMode} data-testid="karaoke-play-btn"
                className="rounded-md bg-brand-amber p-2.5 text-black amber-glow disabled:opacity-40">
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <button onClick={() => jumpLine(1)} data-testid="karaoke-next-line" title="Next line"
                className="rounded-md border border-line bg-bg-elevated p-2 text-ink-secondary hover:text-brand-amber transition">
                <SkipForward className="h-4 w-4" />
              </button>
            </div>
            <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
              Text size
              <input type="range" min="26" max="72" value={fontSize} onChange={(e) => setFontSize(+e.target.value)}
                data-testid="karaoke-size-slider" className="w-28 accent-[#f5a623]" />
            </label>
            <div className="ml-auto">
              <button onClick={toggleFullscreen} data-testid="karaoke-fullscreen-btn" title="Fullscreen"
                className="rounded-md border border-line bg-bg-elevated p-2 text-ink-secondary hover:text-brand-amber transition">
                {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-line bg-bg-surface p-3 text-[11.5px] text-ink-secondary" data-testid="karaoke-audio-note">
        This module displays timed text only. Microphone audio, mixing and playout are handled by your station's own hardware — BroadcastFlow AI never captures or processes any audio signal here.
      </div>

      {track?.audio_reference && (
        <audio
          ref={audioRef}
          src={track.audio_reference}
          onTimeUpdate={(e) => { if (!tapMode) setCurrentMs(e.target.currentTime * 1000); }}
          onEnded={() => setPlaying(false)}
        />
      )}

      {liveOpen && live?.live_id && (
        <LiveQrModal
          live={live}
          trackTitle={track?.title}
          onClose={() => setLiveOpen(false)}
          onStop={stopLive}
        />
      )}

      {createOpen && (
        <CreateTrackModal
          onClose={() => setCreateOpen(false)}
          onCreated={async (t) => { setCreateOpen(false); await load(); setTrack(t); }}
        />
      )}
    </div>
  );
}

function CreateTrackModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", artist: "", source_type: "own_content", bpm: "", audio_reference: "", lyrics_text: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.lyrics_text.trim()) { toast.error("Paste some lyrics first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/karaoke-prompter/tracks", {
        ...form,
        bpm: form.bpm ? parseInt(form.bpm, 10) : null,
      });
      toast.success("Track created — now sync the timing");
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create track"); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="karaoke-create-modal">
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border border-line bg-bg-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-lg">New karaoke track</div>
          <button onClick={onClose} data-testid="karaoke-create-close"
            className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Title *</label>
            <input value={form.title} onChange={set("title")} data-testid="kc-title"
              className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Artist</label>
            <input value={form.artist} onChange={set("artist")} data-testid="kc-artist"
              className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Source</label>
            <select value={form.source_type} onChange={set("source_type")} data-testid="kc-source"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-2 text-sm outline-none">
              <option value="own_content">Your own content</option>
              <option value="manual">Manual entry (you hold display rights)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">BPM (optional)</label>
            <input value={form.bpm} onChange={set("bpm")} data-testid="kc-bpm" placeholder="120"
              className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60" />
          </div>
        </div>
        <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Audio URL (optional — drives sync playback)</label>
        <input value={form.audio_reference} onChange={set("audio_reference")} data-testid="kc-audio"
          placeholder="e.g. a Karaoke Studio instrumental link"
          className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60 mb-3" />
        <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Lyrics (one line per row) *</label>
        <textarea value={form.lyrics_text} onChange={set("lyrics_text")} rows={7} data-testid="kc-lyrics"
          placeholder={"First line of the song\nSecond line of the song"}
          className="w-full resize-y rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60" />
        <div className="mt-2 text-[10px] text-ink-muted">
          By adding lyrics you confirm your station holds the rights to display them. BroadcastFlow AI doesn't fetch or license lyrics on your behalf.
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={save} disabled={saving} data-testid="kc-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create track
          </button>
        </div>
      </div>
    </div>
  );
}

function LiveQrModal({ live, trackTitle, onClose, onStop }) {
  const url = `${window.location.origin}${live.share_path || `/karaoke/${live.live_id}`}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — read it on air or drop it in your socials");
    } catch { toast.error("Could not copy the link"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="live-qr-modal">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-xl border border-line bg-bg-surface p-6 text-center">
        <div className="flex items-center justify-between mb-1">
          <div className="inline-flex items-center gap-2 font-display font-bold text-lg">
            <span className="h-2 w-2 rounded-full bg-rose-400 animate-pulse" /> Karaoke Night is live
          </div>
          <button onClick={onClose} data-testid="live-modal-close"
            className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {trackTitle && <div className="text-xs text-ink-secondary text-left mb-2">Now broadcasting: <span className="text-brand-amber font-semibold">{trackTitle}</span></div>}
        <div className="mx-auto my-4 w-fit rounded-xl bg-white p-4" data-testid="live-qr-code">
          <QRCodeSVG value={url} size={210} />
        </div>
        <div className="font-mono text-[11px] text-brand-amber break-all mb-4" data-testid="live-url">{url}</div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={copy} data-testid="live-copy-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-line bg-bg-elevated py-2 text-sm font-semibold text-ink-secondary hover:text-brand-amber transition">
            <Copy className="h-3.5 w-3.5" /> Copy link
          </button>
          <button onClick={onStop} data-testid="live-stop-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-rose-500/50 bg-rose-500/10 py-2 text-sm font-semibold text-rose-300 hover:bg-rose-500/20 transition">
            <Square className="h-3.5 w-3.5" /> End the night
          </button>
        </div>
        <div className="mt-4 text-[11px] text-ink-secondary text-left">
          Listeners scan the QR (or open the link) and the synced lyrics follow your play position on their phones — about a 2-second delay. Switching tracks here and hitting "Go live" again updates everyone automatically.
        </div>
      </div>
    </div>
  );
}
